// ApiDocumentation.js - Componente de documentación de API
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Shield,
  FileText,
  CheckCircle,
  Database,
  Palette,
  Lightbulb,
  Copy,
  Check,
  ExternalLink,
  Home,
  LogIn,
  Code,
  Terminal,
  Smartphone,
  Coffee,
  Globe,
  Mail,
  Phone,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';

// Importar logo
import logoMafy from '../assets/images/logo-mafy.svg';

const ApiDocumentation = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeEndpoint, setActiveEndpoint] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState('javascript');

  const apiSections = [
    { id: 'overview', title: 'Introducción', icon: BookOpen },
    { id: 'auth', title: 'Autenticación', icon: Shield },
    { id: 'cfdi', title: 'CFDI', icon: FileText },
    { id: 'validation', title: 'Validación', icon: CheckCircle },
    { id: 'catalogs', title: 'Catálogos', icon: Database },
    { id: 'rendering', title: 'Renderizado', icon: Palette },
    { id: 'examples', title: 'Ejemplos', icon: Lightbulb }
  ];

  const endpoints = {
    auth: [
      { method: 'POST', path: '/api/auth/login', desc: 'Iniciar sesión' },
      { method: 'POST', path: '/api/auth/refresh', desc: 'Renovar token' },
      { method: 'POST', path: '/api/auth/logout', desc: 'Cerrar sesión' }
    ],
    cfdi: [
      { method: 'POST', path: '/api/cfdi/stamp', desc: 'Timbrar CFDI' },
      { method: 'POST', path: '/api/cfdi/cancel', desc: 'Cancelar CFDI' },
      { method: 'POST', path: '/api/cfdi/validate-rfc', desc: 'Validar RFC' },
      { method: 'GET', path: '/api/cfdi/status/{uuid}', desc: 'Estado de CFDI' }
    ],
    validation: [
      { method: 'POST', path: '/api/validation/xsd', desc: 'Validación XSD' },
      { method: 'POST', path: '/api/validation/certificate', desc: 'Validar certificado' },
      { method: 'POST', path: '/api/validation/signature', desc: 'Validar firma digital' }
    ],
    catalogs: [
      { method: 'GET', path: '/api/catalogs/forma-pago', desc: 'Formas de pago' },
      { method: 'GET', path: '/api/catalogs/regimen-fiscal', desc: 'Regímenes fiscales' },
      { method: 'GET', path: '/api/catalogs/uso-cfdi', desc: 'Usos de CFDI' }
    ],
    rendering: [
      { method: 'POST', path: '/api/rendering/html', desc: 'Convertir a HTML' },
      { method: 'POST', path: '/api/rendering/pdf', desc: 'Convertir a PDF' },
      { method: 'POST', path: '/api/rendering/json', desc: 'Convertir a JSON' }
    ]
  };

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl mb-6">
          <Code className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          SatCfdi API
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          API REST completa para el manejo de Comprobantes Fiscales Digitales por Internet (CFDI) del SAT mexicano
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card-hover p-6 text-center group">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Creación y Timbrado
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Crea y timbra CFDIs con validación automática
          </p>
        </div>

        <div className="card-hover p-6 text-center group">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Validación Avanzada
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Validación XSD, certificados digitales y firmas
          </p>
        </div>

        <div className="card-hover p-6 text-center group">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Catálogos SAT
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Acceso completo a catálogos oficiales del SAT
          </p>
        </div>

        <div className="card-hover p-6 text-center group">
          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Palette className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Renderizado
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Conversión a PDF, HTML y JSON
          </p>
        </div>
      </div>

      {/* API Info */}
      <div className="card p-8 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Información de la API
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Globe className="w-5 h-5 text-primary-500" />
              <span className="font-semibold text-gray-900 dark:text-white">URL Base:</span>
            </div>
            <code className="text-sm bg-gray-200 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200">
              https://localhost:50403/api
            </code>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="font-semibold text-gray-900 dark:text-white">Autenticación:</span>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200">
              JWT Bearer Token
            </span>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Code className="w-5 h-5 text-blue-500" />
              <span className="font-semibold text-gray-900 dark:text-white">Formato:</span>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
              JSON
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAuth = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <Shield className="w-8 h-8 text-primary-500 mr-3" />
          Autenticación
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          La API utiliza JWT (JSON Web Tokens) para autenticación.
        </p>
      </div>

      {/* Auth Flow */}
      <div className="card p-8">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Flujo de Autenticación
        </h3>
        <div className="space-y-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Login</h4>
              <p className="text-gray-600 dark:text-gray-400">
                Envía credenciales a <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-sm">POST /api/auth/login</code>
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Obtener Token</h4>
              <p className="text-gray-600 dark:text-gray-400">
                Recibe <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-sm">access_token</code> y{' '}
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-sm">refresh_token</code>
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Usar Token</h4>
              <p className="text-gray-600 dark:text-gray-400">
                Incluye en header: <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-sm">Authorization: Bearer &lt;token&gt;</code>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Code Example */}
      <div className="card p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Ejemplo de Login
          </h3>
          <button
            onClick={() => copyToClipboard(`curl -X POST "https://localhost:50403/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "usuario@ejemplo.com",
    "password": "contraseña123"
  }'`, 'auth-example')}
            className="btn-ghost text-sm"
          >
            {copiedCode === 'auth-example' ? (
              <><Check className="w-4 h-4 mr-1" /> Copiado</>
            ) : (
              <><Copy className="w-4 h-4 mr-1" /> Copiar</>
            )}
          </button>
        </div>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
          <code>{`curl -X POST "https://localhost:50403/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "usuario@ejemplo.com",
    "password": "contraseña123"
  }'`}</code>
        </pre>
      </div>
    </div>
  );

  const renderEndpoints = (category) => {
    const section = apiSections.find(s => s.id === category);
    const IconComponent = section?.icon;

    return (
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
            {IconComponent && <IconComponent className="w-8 h-8 text-primary-500 mr-3" />}
            {section?.title}
          </h2>
        </div>

        <div className="space-y-4">
          {endpoints[category]?.map((endpoint, index) => (
            <div key={index} className="card-hover p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${endpoint.method === 'GET' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200' :
                      endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200' :
                        endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                          'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200'
                    }`}>
                    {endpoint.method}
                  </span>
                  <code className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded text-gray-800 dark:text-gray-200">
                    {endpoint.path}
                  </code>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-gray-600 dark:text-gray-400">{endpoint.desc}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Función para copiar código
  const copyToClipboard = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  const renderExamples = () => {
    const languages = [
      { id: 'javascript', name: 'JavaScript', icon: Code },
      { id: 'php', name: 'PHP', icon: Code },
      { id: 'csharp', name: 'C#', icon: Code },
      { id: 'java', name: 'Java', icon: Coffee },
      { id: 'curl', name: 'cURL', icon: Terminal }
    ];

    const examples = {
      javascript: {
        login: `// 1. Autenticarse
const loginResponse = await fetch('https://localhost:50403/api/auth/login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'usuario@ejemplo.com',
    password: 'contraseña123'
  })
});

const { data } = await loginResponse.json();
const accessToken = data.accessToken;`,

        stamp: `// 2. Timbrar CFDI
const stampResponse = await fetch('https://localhost:50403/api/cfdi/stamp', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${accessToken}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    clientId: "123e4567-e89b-12d3-a456-426614174000",
    cfdiData: {
      comprobante: {
        version: "4.0",
        tipoDeComprobante: "I",
        total: 1160.00,
        subtotal: 1000.00,
        emisor: {
          rfc: "XAXX010101000",
          nombre: "Empresa Ejemplo"
        },
        receptor: {
          rfc: "XEXX010101000",
          nombre: "Cliente Ejemplo"
        }
      }
    }
  })
});

const result = await stampResponse.json();
console.log('UUID:', result.data.uuid);`
      },
      curl: {
        login: `# 1. Autenticarse
curl -X POST "https://localhost:50403/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "usuario@ejemplo.com",
    "password": "contraseña123"
  }'`,

        stamp: `# 2. Timbrar CFDI
curl -X POST "https://localhost:50403/api/cfdi/stamp" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "clientId": "123e4567-e89b-12d3-a456-426614174000",
    "cfdiData": {
      "comprobante": {
        "version": "4.0",
        "tipoDeComprobante": "I",
        "total": 1160.00,
        "subtotal": 1000.00
      }
    }
  }'`
      }
    };

    return (
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
            <Lightbulb className="w-8 h-8 text-primary-500 mr-3" />
            Ejemplos de Uso
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Ejemplos prácticos de cómo integrar la API en diferentes lenguajes de programación.
          </p>
        </div>

        {/* Language Selector */}
        <div className="flex flex-wrap gap-2">
          {languages.map((lang) => (
            <button
              key={lang.id}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${activeLanguage === lang.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              onClick={() => setActiveLanguage(lang.id)}
            >
              <lang.icon className="w-4 h-4" />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>

        {/* Examples */}
        <div className="space-y-8">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                1. Autenticación
              </h3>
              <button
                onClick={() => copyToClipboard(examples[activeLanguage]?.login || '', 'login')}
                className="btn-ghost text-sm"
              >
                {copiedCode === 'login' ? (
                  <><Check className="w-4 h-4 mr-1" /> Copiado</>
                ) : (
                  <><Copy className="w-4 h-4 mr-1" /> Copiar</>
                )}
              </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{examples[activeLanguage]?.login || ''}</code>
            </pre>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                2. Timbrar CFDI
              </h3>
              <button
                onClick={() => copyToClipboard(examples[activeLanguage]?.stamp || '', 'stamp')}
                className="btn-ghost text-sm"
              >
                {copiedCode === 'stamp' ? (
                  <><Check className="w-4 h-4 mr-1" /> Copiado</>
                ) : (
                  <><Copy className="w-4 h-4 mr-1" /> Copiar</>
                )}
              </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{examples[activeLanguage]?.stamp || ''}</code>
            </pre>
          </div>
        </div>

        {/* Response Examples */}
        <div className="card p-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            📤 Ejemplos de Respuestas
          </h3>

          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Respuesta de Login Exitoso
              </h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{`{
  "success": true,
  "data": {
    "accessToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
    "refreshToken": "refresh_token_here",
    "tokenType": "Bearer",
    "expiresIn": 3600,
    "user": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "email": "usuario@ejemplo.com",
      "role": "User"
    }
  }
}`}</code>
              </pre>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="card p-8">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            📚 Recursos Adicionales
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/login" className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <LogIn className="w-5 h-5 text-primary-500" />
              <span className="text-gray-700 dark:text-gray-300">Acceder al Sistema</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <Home className="w-5 h-5 text-primary-500" />
              <span className="text-gray-700 dark:text-gray-300">Página Principal</span>
            </Link>
            <a href="mailto:soporte@mafy.mx" className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <Mail className="w-5 h-5 text-primary-500" />
              <span className="text-gray-700 dark:text-gray-300">Soporte Técnico</span>
            </a>
            <a href="tel:+52-55-1234-5678" className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <Phone className="w-5 h-5 text-primary-500" />
              <span className="text-gray-700 dark:text-gray-300">Contacto Directo</span>
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverview();
      case 'auth':
        return renderAuth();
      case 'examples':
        return renderExamples();
      default:
        return renderEndpoints(activeSection);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src={logoMafy}
                alt="Mafy"
                className="h-10 w-10 transition-transform duration-200 group-hover:scale-110"
              />
              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                Mafy
              </span>
            </Link>

            <div className="flex items-center space-x-4">
              <Link to="/" className="nav-link">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
              <Link to="/login" className="btn-primary">
                <LogIn className="w-4 h-4 mr-2" />
                Iniciar Sesión
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white dark:bg-gray-800 h-screen sticky top-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Documentación API
            </h3>
            <nav className="space-y-2">
              {apiSections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors duration-200 ${activeSection === section.id
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-600'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">{section.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* External Links */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              Enlaces Rápidos
            </h4>
            <div className="space-y-2">
              <Link
                to="/"
                className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Volver al Inicio</span>
              </Link>
              <Link
                to="/login"
                className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span>Acceder al Sistema</span>
              </Link>
              <a
                href="mailto:soporte@mafy.mx"
                className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>Soporte</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <div className="max-w-4xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;